import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, mapTo, map } from 'rxjs/operators';
import { BackendApiResponse, DEFAULT_REQUEST_PARAMS, EntityId } from '@shared/core/types';
import { AppApiService } from '@shared/core';
import {
  EducationServiceVacationCreateRequest,
  EducationServiceVacationUpdateRequest,
  EducationServiceVacation,
  EducationServiceVacationsResponse,
} from '@crm/shared/persons/types';
import { environment } from '@environment';
import { convertApiItemResponseToItem } from '@shared/core/utils';

@Injectable({
  providedIn: 'root',
})
export class VacationManagementApiService {
  protected baseEndpoint = environment.studentVacation.api;

  constructor(protected api: AppApiService) {}

  getEndedStudentVacations(serviceId: EntityId): Observable<EducationServiceVacation[]> {
    const url = `${this.baseEndpoint}v1/education-services/${serviceId}/student-vacations/ended`;
    return this.api.get<EducationServiceVacationsResponse>(url).pipe(
      convertApiItemResponseToItem,
      catchError(() => of([])),
    );
  }

  getCurrentOrFutureStudentVacation(serviceId: EntityId): Observable<EducationServiceVacation> {
    const url = `${this.baseEndpoint}v1/education-services/${serviceId}/student-vacations/current-or-future`;
    return this.api.get<BackendApiResponse<EducationServiceVacation>>(url).pipe(
      convertApiItemResponseToItem,
      catchError(() => of(null)),
    );
  }

  isSupportedServiceTypeKey(serviceTypeKey: string): Observable<boolean> {
    const url = `${this.baseEndpoint}v2/is-allow-self-schedule/${serviceTypeKey}`;
    return this.api.get<{ data: boolean }>(url).pipe(
      convertApiItemResponseToItem,
      catchError(() => of(null)),
    );
  }

  getReasonsByStk(stk: string): Observable<string[]> {
    const url = `${this.baseEndpoint}v1/reason/${stk}`;
    return this.api.get<BackendApiResponse<string[]>>(url).pipe(
      convertApiItemResponseToItem,
      catchError(() => of(null)),
    );
  }

  sendOnVacation(serviceId: EntityId, data: EducationServiceVacationCreateRequest): Observable<boolean> {
    return this.api
      .post<any>(`${this.baseEndpoint}v1/education-services/${serviceId}/student-vacations`, {
        params: data,
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(
        mapTo(true),
        catchError((error: HttpErrorResponse) => throwError(error.error)),
      );
  }

  changeVacationDates(serviceId: EntityId, data: EducationServiceVacationUpdateRequest): Observable<boolean> {
    return this.api
      .put<any>(`${this.baseEndpoint}v1/education-services/${serviceId}/student-vacations/future`, {
        params: data,
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(
        mapTo(true),
        catchError((error: HttpErrorResponse) => throwError(error.error)),
      );
  }

  cancelVacation(serviceId: EntityId): Observable<boolean> {
    return this.api
      .delete<any>(`${this.baseEndpoint}v1/education-services/${serviceId}/student-vacations/current-or-future`, {
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(
        mapTo(true),
        catchError((error: HttpErrorResponse) => throwError(error.error)),
      );
  }

  returnFromVacation(serviceId: EntityId): Observable<boolean> {
    return this.api
      .delete<any>(`${this.baseEndpoint}v1/education-services/${serviceId}/student-vacations/current-or-future`, {
        ...DEFAULT_REQUEST_PARAMS,
      })
      .pipe(
        mapTo(true),
        catchError((error: HttpErrorResponse) => throwError(error.error)),
      );
  }
}
